import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Package, Edit, Trash2, Search, Upload,
  ChevronRight, ChevronDown, Lightbulb, Wrench, FlaskConical, Monitor,
  Wheat, FolderOpen, FileSpreadsheet, X, Filter, LayoutGrid, List, Eye
} from 'lucide-react';
import {
  listProducts, createProduct, updateProduct, deleteProduct,
  getCategoryTree, getSpecTemplates, importProductsCsv, getManufacturersList
} from '../lib/api';

const BRANCH_ICONS = { Lightbulb, Wrench, FlaskConical, Monitor, Wheat, FolderOpen, Package };

/**
 * BUG #2 fix (2026-05-11): cena może być w jednym z 4 pól:
 *   - cost_price (preferred, post P4 fix) — to nasza cena zakupu
 *   - base_price (RRP/list, np. AQform pełen seed)
 *   - estimated_list_price_pln (heurystyka z power+lumens)
 *   - price (legacy field)
 * Zwraca: { value, source, currency }
 */
function getProductPrice(p) {
  const currency = p.currency || 'PLN';
  if (p.cost_price && p.cost_price > 0) {
    return { value: p.cost_price, source: p.price_source || 'cost', currency, label: 'Cena zakupu' };
  }
  if (p.base_price && p.base_price > 0) {
    return { value: p.base_price, source: 'list', currency, label: 'Cena katalogowa' };
  }
  if (p.estimated_list_price_pln && p.estimated_list_price_pln > 0) {
    return { value: p.estimated_list_price_pln, source: 'estimated', currency, label: 'Cena szacowana' };
  }
  if (p.price && p.price > 0) {
    return { value: p.price, source: 'legacy', currency, label: 'Cena' };
  }
  return null;
}

function fmtPrice(p) {
  const pr = getProductPrice(p);
  if (!pr) return '—';
  return `${Math.round(pr.value).toLocaleString('pl-PL')} ${pr.currency}`;
}


// ===== CATEGORY SIDEBAR =====
function CategorySidebar({ lang, tree, activeCategory, onSelect, loading }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div className="space-y-2 p-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-8 w-full" />)}</div>;

  const branches = tree?.branches || [];

  return (
    <div className="space-y-1">
      <button onClick={() => onSelect(null)}
        className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${!activeCategory ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted text-foreground'}`}>
        <Package className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1">{lang === 'pl' ? 'Wszystkie produkty' : 'All products'}</span>
        <span className="text-xs text-muted-foreground">{tree?.total_products || 0}</span>
      </button>
      <div className="h-px bg-border my-2" />
      {branches.map(branch => {
        const IconComp = BRANCH_ICONS[branch.icon] || Package;
        const isExpanded = expanded[branch.id] !== false;
        const branchCount = branch.categories.reduce((s, c) => s + (c.product_count || 0), 0);
        return (
          <div key={branch.id}>
            <button onClick={() => toggle(branch.id)}
              className="w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-muted font-medium">
              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              <IconComp className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">{lang === 'pl' ? branch.name_pl : branch.name_en}</span>
              {branchCount > 0 && <span className="text-xs text-muted-foreground">{branchCount}</span>}
            </button>
            {isExpanded && branch.categories.map(cat => (
              <div key={cat.id}>
                <button onClick={() => onSelect(cat.name_pl)}
                  className={`w-full text-left pl-9 pr-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${activeCategory === cat.name_pl ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted text-muted-foreground'}`}>
                  <span className="flex-1 truncate">{lang === 'pl' ? cat.name_pl : cat.name_en}</span>
                  {cat.product_count > 0 && <span className="text-xs">{cat.product_count}</span>}
                </button>
                {activeCategory === cat.name_pl && cat.subcategories?.length > 0 && (
                  <div className="ml-9 pl-3 border-l border-border/50 mt-0.5 mb-1 space-y-0.5">
                    {cat.subcategories.map((sub, i) => (
                      <button key={i} onClick={() => onSelect(sub)}
                        className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${activeCategory === sub ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                        {sub}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ===== SPEC EDITOR =====
function SpecEditor({ specs, onChange, templates, lang }) {
  const lightingSpecs = templates?.lighting?.common_specs || [];
  const updateSpec = (index, field, value) => { const u = [...specs]; u[index] = { ...u[index], [field]: value }; onChange(u); };
  const addSpec = (tmpl) => {
    if (tmpl) { if (!specs.find(s => s.key === tmpl.key)) onChange([...specs, { key: tmpl.key, value: '', unit: tmpl.unit || '' }]); }
    else onChange([...specs, { key: '', value: '', unit: '' }]);
  };
  const removeSpec = (index) => onChange(specs.filter((_, i) => i !== index));
  const availableTemplates = lightingSpecs.filter(t => !specs.find(s => s.key === t.key));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">{lang === 'pl' ? 'Specyfikacja techniczna' : 'Technical specifications'}</Label>
        <div className="flex gap-1">
          {availableTemplates.length > 0 && (
            <Select onValueChange={(key) => { const tm = lightingSpecs.find(t => t.key === key); if (tm) addSpec(tm); }}>
              <SelectTrigger className="h-7 text-xs w-[180px]">
                <SelectValue placeholder={lang === 'pl' ? '+ Dodaj parametr...' : '+ Add parameter...'} />
              </SelectTrigger>
              <SelectContent>
                {availableTemplates.map(t => <SelectItem key={t.key} value={t.key}>{lang === 'pl' ? t.label_pl : t.label_en} {t.unit ? `(${t.unit})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addSpec(null)}>
            <Plus className="w-3 h-3 mr-1" />{lang === 'pl' ? 'Wlasny' : 'Custom'}
          </Button>
        </div>
      </div>
      {specs.length === 0 && <p className="text-xs text-muted-foreground italic py-2">{lang === 'pl' ? 'Brak specyfikacji. Dodaj parametry powyzej.' : 'No specs yet. Add parameters above.'}</p>}
      <div className="space-y-1.5">
        {specs.map((spec, i) => {
          const tmpl = lightingSpecs.find(t => t.key === spec.key);
          return (
            <div key={i} className="flex gap-2 items-center">
              {tmpl ? <div className="w-[140px] text-xs text-muted-foreground truncate">{lang === 'pl' ? tmpl.label_pl : tmpl.label_en}</div>
                : <Input value={spec.key} onChange={e => updateSpec(i, 'key', e.target.value)} className="w-[140px] h-7 text-xs" placeholder={lang === 'pl' ? 'Parametr' : 'Parameter'} />}
              {tmpl?.type === 'select' ? (
                <Select value={spec.value} onValueChange={v => updateSpec(i, 'value', v)}>
                  <SelectTrigger className="flex-1 h-7 text-xs"><SelectValue placeholder="..." /></SelectTrigger>
                  <SelectContent>{(tmpl.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={spec.value} onChange={e => updateSpec(i, 'value', e.target.value)} className="flex-1 h-7 text-xs"
                  placeholder={lang === 'pl' ? 'Wartosc' : 'Value'} type={tmpl?.type === 'number' ? 'number' : 'text'} />
              )}
              {(tmpl?.unit || spec.unit) && <span className="text-xs text-muted-foreground w-8">{tmpl?.unit || spec.unit}</span>}
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeSpec(i)}><X className="w-3 h-3" /></Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== CSV IMPORT DIALOG =====
function CsvImportDialog({ open, onClose, lang, tenantId, onImported }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f && (f.name.endsWith('.csv') || f.type === 'text/csv')) { setFile(f); setResult(null); }
    else toast.error(lang === 'pl' ? 'Wybierz plik CSV' : 'Select a CSV file');
  }, [lang]);

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await importProductsCsv(file, tenantId);
      setResult(res);
      toast.success(lang === 'pl' ? `Zaimportowano ${res.imported} produktow` : `Imported ${res.imported} products`);
      onImported();
    } catch (err) { toast.error(lang === 'pl' ? 'Blad importu CSV' : 'CSV import error'); }
    setImporting(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setFile(null); setResult(null); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            {lang === 'pl' ? 'Import katalogu CSV' : 'CSV Catalog Import'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div onDrop={handleDrop} onDragOver={e => e.preventDefault()} onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors">
            <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            {file ? (
              <div><p className="text-sm font-medium">{file.name}</p><p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p></div>
            ) : (
              <div>
                <p className="text-sm">{lang === 'pl' ? 'Przeciagnij plik CSV lub kliknij' : 'Drop CSV file or click'}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {lang === 'pl' ? 'Kolumny: name/nazwa, sku/kod, category/kategoria, price/cena, lumen, watt, kelvin, cri, ip, tags, certifications'
                    : 'Columns: name/nazwa, sku/kod, category/kategoria, price/cena, lumen, watt, kelvin, cri, ip, tags, certifications'}
                </p>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e => { setFile(e.target.files[0]); setResult(null); }} />
          </div>
          <div className="bg-muted/50 rounded-md p-3">
            <p className="text-xs font-medium mb-1">{lang === 'pl' ? 'Przykladowy format:' : 'Example format:'}</p>
            <pre className="text-[10px] text-muted-foreground overflow-x-auto">
{`name,sku,category,price,lumen,watt,kelvin,cri,ip,certifications,tags
"Panel LED Pro",PLP-001,Oprawy LED,120,4000,36,4000,80,IP20,"CE,ENEC","modern,panel"`}
            </pre>
          </div>
          {result && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-md p-3">
              <p className="text-sm text-green-400">
                {lang === 'pl' ? `Zaimportowano ${result.imported} z ${result.total_in_file} produktow` : `Imported ${result.imported} of ${result.total_in_file} products`}
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { onClose(); setFile(null); setResult(null); }}>{lang === 'pl' ? 'Zamknij' : 'Close'}</Button>
            <Button onClick={handleImport} disabled={!file || importing} className="gap-2">
              <Upload className="w-4 h-4" />
              {importing ? (lang === 'pl' ? 'Importowanie...' : 'Importing...') : (lang === 'pl' ? 'Importuj' : 'Import')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== PRODUCT DETAIL VIEW =====
function ProductDetail({ product, lang, onClose, onEdit }) {
  if (!product) return null;

  // Handle both formats: product.specs (array) and product.specifications (object)
  const SPEC_LABELS = {
    power: { pl: 'Moc', en: 'Power', unit: 'W' },
    lumens: { pl: 'Strumień świetlny', en: 'Luminous flux', unit: 'lm' },
    cri: { pl: 'CRI/Ra', en: 'CRI/Ra', unit: '' },
    cct_k: { pl: 'Barwa', en: 'Color temp', unit: 'K' },
    ip_rating: { pl: 'Stopień IP', en: 'IP Rating', unit: '' },
    efficacy_lm_w: { pl: 'Skuteczność', en: 'Efficacy', unit: 'lm/W' },
  };

  let specEntries = [];
  if (product.specs && Array.isArray(product.specs) && product.specs.length > 0) {
    // Old format: array of {key, value, unit}
    specEntries = product.specs;
  } else if (product.specifications && typeof product.specifications === 'object') {
    // New format: object {power, lumens, cri, cct_k, ip_rating, efficacy_lm_w}
    specEntries = Object.entries(product.specifications)
      .filter(([k, v]) => v && v !== 0 && v !== '')
      .map(([k, v]) => ({
        key: SPEC_LABELS[k] ? (lang === 'pl' ? SPEC_LABELS[k].pl : SPEC_LABELS[k].en) : k,
        value: v,
        unit: SPEC_LABELS[k]?.unit || '',
      }));
  }

  const certs = product.certifications || [];
  const tags = product.tags || [];

  return (
    <Dialog open={!!product} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5" />{product.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            {product.manufacturer && <Badge className="bg-blue-500/20 text-blue-400">{product.manufacturer}</Badge>}
            {product.sku && <Badge variant="outline" className="font-mono text-xs">SKU: {product.sku}</Badge>}
            {product.category && <Badge variant="secondary">{product.category}</Badge>}
            {product.style && <Badge variant="outline" className="text-xs">{product.style}</Badge>}
          </div>
          {product.short_description && <p className="text-sm text-muted-foreground">{product.short_description}</p>}
          {product.description && <p className="text-sm">{product.description}</p>}

          {/* Specifications — works with both old (specs array) and new (specifications object) format */}
          {specEntries.length > 0 && (
            <div>
              <h4 className="text-xs font-medium mb-2 uppercase tracking-wider">{lang === 'pl' ? 'Specyfikacja techniczna' : 'Technical Specifications'}</h4>
              <div className="grid grid-cols-2 gap-1">
                {specEntries.map((s, i) => (
                  <div key={i} className="flex justify-between text-xs bg-muted/30 rounded px-2 py-1.5">
                    <span className="text-muted-foreground">{s.key}</span>
                    <span className="font-medium">{s.value}{s.unit ? ` ${s.unit}` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Price (BUG #2 fix 2026-05-11) — pokazuje cost_price / base_price / estimated z odpowiednim label */}
          {(() => {
            const pr = getProductPrice(product);
            if (!pr) return null;
            return (
              <div className="bg-muted/50 rounded-md p-3 space-y-1">
                <div>
                  <span className="text-xs text-muted-foreground">{pr.label}: </span>
                  <span className="text-lg font-semibold">{Math.round(pr.value).toLocaleString('pl-PL')} {pr.currency}</span>
                </div>
                {pr.source && pr.source !== 'cost' && pr.source !== 'list' && (
                  <div className="text-[10px] text-amber-400">
                    Źródło: {pr.source === 'estimated' || pr.source === 'estimated_from_specs'
                      ? '🟡 szacunek z parametrów' : pr.source}
                  </div>
                )}
                {product.cost_price && product.base_price > 0 && product.cost_price !== product.base_price && (
                  <div className="text-[10px] text-muted-foreground">
                    Cena katalogowa: {Math.round(product.base_price).toLocaleString('pl-PL')} {pr.currency}
                    {product.distributor_margin_pct && ` (-${product.distributor_margin_pct}% marża)`}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Certifications & tags */}
          {(certs.length > 0 || tags.length > 0) && (
            <div className="flex flex-wrap gap-1.5">
              {certs.map((c, i) => <Badge key={`c${i}`} variant="outline" className="text-xs">{c}</Badge>)}
              {tags.map((tg, i) => <Badge key={`t${i}`} className="text-xs bg-blue-500/20 text-blue-400">{tg}</Badge>)}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-between items-center pt-2">
            <div>
              {product.product_url && (
                <a href={product.product_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors">
                  <Eye className="w-3.5 h-3.5" />
                  {lang === 'pl' ? 'Strona producenta' : 'Manufacturer page'}
                </a>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>{lang === 'pl' ? 'Zamknij' : 'Close'}</Button>
              <Button onClick={() => onEdit(product)} className="gap-2"><Edit className="w-4 h-4" />{lang === 'pl' ? 'Edytuj' : 'Edit'}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== MAIN PRODUCTS PAGE =====
export default function Products({ lang, tenantId }) {
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [tree, setTree] = useState(null);
  const [treeLoading, setTreeLoading] = useState(true);
  const [specTemplates, setSpecTemplates] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [manufacturers, setManufacturers] = useState([]);
  const [activeManufacturer, setActiveManufacturer] = useState(null);
  const [form, setForm] = useState({
    name: '', sku: '', category: '', description: '', short_description: '',
    base_price: 0, currency: 'PLN', unit: 'szt', min_order: 1,
    stock_quantity: 0, stock_unit: 'szt', certifications: [], tags: [], specs: [], active: true
  });

  useEffect(() => { loadTree(); loadSpecTemplates(); loadManufacturers(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadProducts(); }, [tenantId, search, activeCategory, activeManufacturer]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadProducts() {
    setLoading(true);
    try {
      const res = await listProducts(tenantId, search || null, activeCategory || null, 0, 100, activeManufacturer || null);
      setProducts(res.products || res);
      setTotalProducts(res.total ?? (res.products || res).length);
    } catch (err) { console.error(err); }
    setLoading(false);
  }
  async function loadManufacturers() {
    try { setManufacturers(await getManufacturersList(tenantId)); } catch (err) { console.error(err); }
  }
  async function loadTree() {
    setTreeLoading(true);
    try { setTree(await getCategoryTree(tenantId)); } catch (err) { console.error(err); }
    setTreeLoading(false);
  }
  async function loadSpecTemplates() {
    try { setSpecTemplates(await getSpecTemplates()); } catch (err) { console.error(err); }
  }

  async function handleSave() {
    if (!form.name.trim()) { toast.error(lang === 'pl' ? 'Nazwa produktu jest wymagana' : 'Product name required'); return; }
    try {
      if (editProduct) { await updateProduct(editProduct.id, form); toast.success(lang === 'pl' ? 'Produkt zaktualizowany' : 'Product updated'); }
      else { await createProduct(form, tenantId); toast.success(lang === 'pl' ? 'Produkt dodany' : 'Product added'); }
      setDialogOpen(false); setEditProduct(null); resetForm(); loadProducts(); loadTree();
    } catch (err) { toast.error(lang === 'pl' ? 'Blad zapisu' : 'Save error'); }
  }

  async function handleDelete(id) {
    if (!window.confirm(lang === 'pl' ? 'Usunac produkt?' : 'Delete product?')) return;
    try { await deleteProduct(id); toast.success(lang === 'pl' ? 'Usunieto' : 'Deleted'); loadProducts(); loadTree(); }
    catch (err) { toast.error(lang === 'pl' ? 'Blad' : 'Error'); }
  }

  function resetForm() {
    setForm({ name: '', sku: '', category: '', description: '', short_description: '',
      base_price: 0, currency: 'PLN', unit: 'szt', min_order: 1,
      stock_quantity: 0, stock_unit: 'szt', certifications: [], tags: [], specs: [], active: true });
  }

  function openEdit(p) {
    setEditProduct(p);
    setForm({ ...p, certifications: p.certifications || [], tags: p.tags || [], specs: p.specs || [], active: p.active !== false });
    setDialogOpen(true); setViewProduct(null);
  }

  function openCreate(category) {
    setEditProduct(null); resetForm();
    if (category) setForm(prev => ({ ...prev, category }));
    setDialogOpen(true);
  }

  // Build category options for the form dropdown
  const categoryOptions = [];
  if (tree?.branches) {
    for (const branch of tree.branches) {
      for (const cat of branch.categories) {
        categoryOptions.push({ label: lang === 'pl' ? cat.name_pl : cat.name_en, value: cat.name_pl });
        for (const sub of (cat.subcategories || [])) {
          categoryOptions.push({ label: `  ${sub}`, value: sub });
        }
      }
    }
  }

  const knownCerts = specTemplates?.lighting?.certifications || [];

  return (
    <div className="flex gap-6 h-full" data-testid="products-page">
      {/* LEFT: Category sidebar */}
      <div className="w-64 flex-shrink-0 hidden md:block">
        <Card className="border border-border sticky top-4">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="w-4 h-4" />
              {lang === 'pl' ? 'Kategorie' : 'Categories'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 max-h-[calc(100vh-200px)] overflow-y-auto">
            <CategorySidebar lang={lang} tree={tree} activeCategory={activeCategory} onSelect={setActiveCategory} loading={treeLoading} />
          </CardContent>
        </Card>
      </div>

      {/* RIGHT: Product list */}
      <div className="flex-1 space-y-4 min-w-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              {lang === 'pl' ? 'Katalog produktow' : 'Product Catalog'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {activeCategory && <span className="text-primary">{activeCategory} — </span>}
              {totalProducts} {lang === 'pl' ? 'produktow' : 'products'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setCsvOpen(true)}>
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">{lang === 'pl' ? 'Import CSV' : 'CSV Import'}</span>
            </Button>
            {/* T43 — import cennika dystrybutora → realny cost_price (zamiast estymat) */}
            <input
              id="price-list-input" type="file" accept=".xlsx,.csv,.tsv" style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const margin = window.prompt(
                  'Cennik to ceny ZAKUPU czy KATALOGOWE?\n\n' +
                  '• Ceny zakupu (Twoje koszty) → zostaw puste / 0\n' +
                  '• Ceny katalogowe (RRP) → wpisz marżę dystrybutora % (np. 25)',
                  ''
                );
                const marginPct = margin && !isNaN(parseFloat(margin)) ? parseFloat(margin) : null;
                try {
                  toast.info('Importuję cennik — matchuję po SKU...');
                  const { importPriceList } = await import('../lib/api');
                  const r = await importPriceList(f, { matchField: 'sku', marginPct });
                  if (r.error) {
                    toast.error(`${r.error} (wykryte kolumny: ${JSON.stringify(r.detected_columns || {})})`);
                  } else {
                    toast.success(
                      `✅ Cennik: sparsowano ${r.parsed}, dopasowano ${r.matched}, zaktualizowano ${r.updated} produktów` +
                      (r.margin_applied_pct ? ` (marża -${r.margin_applied_pct}%)` : '') +
                      `. Teraz ceny są REALNE, nie estymaty.`
                    );
                    loadProducts();
                  }
                } catch (err) {
                  toast.error('Import cennika nie powiódł się: ' + (err.response?.data?.detail || err.message));
                } finally {
                  e.target.value = '';
                }
              }}
            />
            <Button
              variant="outline" size="sm"
              className="gap-2 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              onClick={() => document.getElementById('price-list-input')?.click()}
              title="Wgraj cennik dystrybutora (XLSX/CSV) → realne ceny zakupu zamiast estymat. Match po SKU."
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">{lang === 'pl' ? 'Wgraj cennik' : 'Price list'}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              onClick={() => document.getElementById('gldf-import-input')?.click()}
              title="Import GLDF z DIALux — auto-uzupełnij specyfikację (W, lumen, IP, K, IRC)"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Import GLDF</span>
            </Button>
            <input
              id="gldf-import-input"
              type="file"
              accept=".gldf,.zip,.xml"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append('file', f);
                try {
                  toast.info('Importuję GLDF — parsuję specyfikację...');
                  const token = localStorage.getItem('token');
                  const r = await fetch('/api/products/import-gldf', {
                    method: 'POST',
                    headers: { Authorization: 'Bearer ' + token },
                    body: fd,
                  });
                  if (!r.ok) throw new Error(await r.text());
                  const data = await r.json();
                  toast.success(`GLDF: ${data.parsed} sparsowane · ${data.created} nowych · ${data.updated} wzbogaconych`);
                  loadProducts && loadProducts();
                } catch (err) {
                  toast.error('Import GLDF nie powiódł się: ' + (err.message || err));
                }
                e.target.value = '';
              }}
            />
            <Button className="gap-2" onClick={() => openCreate(activeCategory)}>
              <Plus className="w-4 h-4" />
              {lang === 'pl' ? 'Dodaj produkt' : 'Add Product'}
            </Button>
          </div>
        </div>

        {/* Search + manufacturer filter + view toggle */}
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative flex-1 max-w-md min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder={lang === 'pl' ? 'Szukaj produktow...' : 'Search products...'} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-3.5 h-3.5 text-muted-foreground" /></button>}
          </div>

          {/* Manufacturer filter */}
          <Select value={activeManufacturer || '__all__'} onValueChange={v => setActiveManufacturer(v === '__all__' ? null : v)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={lang === 'pl' ? 'Producent' : 'Manufacturer'} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="__all__">{lang === 'pl' ? 'Wszyscy producenci' : 'All manufacturers'}</SelectItem>
              {manufacturers.map(m => (
                <SelectItem key={m.name} value={m.name}>{m.name} ({m.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Active filters badges */}
          {(activeCategory || activeManufacturer) && (
            <div className="flex gap-1 items-center">
              {activeCategory && (
                <Badge variant="secondary" className="gap-1 cursor-pointer" onClick={() => setActiveCategory(null)}>
                  {activeCategory} <X className="w-3 h-3" />
                </Badge>
              )}
              {activeManufacturer && (
                <Badge variant="secondary" className="gap-1 cursor-pointer" onClick={() => setActiveManufacturer(null)}>
                  {activeManufacturer} <X className="w-3 h-3" />
                </Badge>
              )}
            </div>
          )}

          <div className="flex border border-border rounded-md overflow-hidden">
            <button onClick={() => setViewMode('table')} className={`p-1.5 ${viewMode === 'table' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}><List className="w-4 h-4" /></button>
            <button onClick={() => setViewMode('grid')} className={`p-1.5 ${viewMode === 'grid' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}><LayoutGrid className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Product list */}
        <Card className="border border-border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  {activeCategory ? (lang === 'pl' ? `Brak produktow w "${activeCategory}"` : `No products in "${activeCategory}"`) : (lang === 'pl' ? 'Brak produktow' : 'No products')}
                </p>
                <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => openCreate(activeCategory)}>
                  <Plus className="w-3.5 h-3.5" />{lang === 'pl' ? 'Dodaj pierwszy produkt' : 'Add first product'}
                </Button>
              </div>
            ) : viewMode === 'table' ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>{lang === 'pl' ? 'Nazwa' : 'Name'}</TableHead>
                    <TableHead>{lang === 'pl' ? 'Kategoria' : 'Category'}</TableHead>
                    <TableHead>{lang === 'pl' ? 'Specyfikacja' : 'Specs'}</TableHead>
                    <TableHead>{lang === 'pl' ? 'Cena' : 'Price'}</TableHead>
                    <TableHead>{lang === 'pl' ? 'Stan' : 'Stock'}</TableHead>
                    <TableHead className="text-right">{lang === 'pl' ? 'Akcje' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map(p => (
                    <TableRow key={p.id} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => setViewProduct(p)}>
                      <TableCell className="font-mono text-xs text-muted-foreground">{p.sku || '-'}</TableCell>
                      <TableCell>
                        <div>
                          <span className="text-sm font-medium">{p.name}</span>
                          {p.short_description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{p.short_description}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs cursor-pointer" onClick={e => { e.stopPropagation(); setActiveCategory(p.category); }}>
                          {p.category || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(p.specs || []).slice(0, 3).map((s, i) => (
                            <span key={i} className="text-[10px] bg-muted/50 rounded px-1.5 py-0.5">{s.key}: {s.value}{s.unit ? ` ${s.unit}` : ''}</span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{fmtPrice(p)}</TableCell>
                      <TableCell className="text-sm">{p.stock_quantity > 0 ? `${p.stock_quantity} ${p.stock_unit || p.unit}` : '-'}</TableCell>
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => setViewProduct(p)}><Eye className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Edit className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 p-4">
                {products.map(p => (
                  <div key={p.id} onClick={() => setViewProduct(p)} className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors cursor-pointer">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-medium line-clamp-2">{p.name}</h3>
                      <div className="flex gap-1 ml-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEdit(p)}><Edit className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    {p.sku && <p className="text-xs text-muted-foreground font-mono mb-1">{p.sku}</p>}
                    {p.category && <Badge variant="secondary" className="text-[10px] mb-2">{p.category}</Badge>}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(p.specs || []).slice(0, 4).map((s, i) => <span key={i} className="text-[10px] bg-muted/50 rounded px-1.5 py-0.5">{s.key}: {s.value}</span>)}
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-2 border-t border-border/50">
                      <span className="text-sm font-semibold">{fmtPrice(p)}</span>
                      <span className="text-xs text-muted-foreground">{p.stock_quantity > 0 ? `${p.stock_quantity} ${p.stock_unit || p.unit}` : '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <ProductDetail product={viewProduct} lang={lang} onClose={() => setViewProduct(null)} onEdit={openEdit} />
      <CsvImportDialog open={csvOpen} onClose={() => setCsvOpen(false)} lang={lang} tenantId={tenantId} onImported={() => { loadProducts(); loadTree(); }} />

      {/* Product Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={v => { setDialogOpen(v); if (!v) setEditProduct(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editProduct ? (lang === 'pl' ? 'Edytuj produkt' : 'Edit Product') : (lang === 'pl' ? 'Nowy produkt' : 'New Product')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{lang === 'pl' ? 'Nazwa' : 'Name'} *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="mt-1" /></div>
              <div><Label className="text-xs">SKU</Label>
                <Input value={form.sku} onChange={e => setForm(p => ({...p, sku: e.target.value}))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'Kategoria' : 'Category'}</Label>
                {categoryOptions.length > 0 ? (
                  <Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={lang === 'pl' ? 'Wybierz kategorie...' : 'Select category...'} /></SelectTrigger>
                    <SelectContent>{categoryOptions.map((opt, i) => <SelectItem key={i} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : null}
                <Input value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} className="mt-1"
                  placeholder={lang === 'pl' ? 'lub wpisz wlasna kategorie...' : 'or type custom category...'} />
              </div>
              <div><Label className="text-xs">{lang === 'pl' ? 'Jednostka' : 'Unit'}</Label>
                <Select value={form.unit} onValueChange={v => setForm(p => ({...p, unit: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{['szt','kg','t','m3','l','m','mb','opak','kpl'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs">{lang === 'pl' ? 'Krotki opis' : 'Short description'}</Label>
              <Input value={form.short_description} onChange={e => setForm(p => ({...p, short_description: e.target.value}))} className="mt-1" /></div>
            <div><Label className="text-xs">{lang === 'pl' ? 'Pelny opis' : 'Full description'}</Label>
              <Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="mt-1 min-h-[60px]" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">{lang === 'pl' ? 'Cena bazowa' : 'Base price'}</Label>
                <Input type="number" value={form.base_price} onChange={e => setForm(p => ({...p, base_price: parseFloat(e.target.value) || 0}))} className="mt-1" /></div>
              <div><Label className="text-xs">{lang === 'pl' ? 'Waluta' : 'Currency'}</Label>
                <Select value={form.currency} onValueChange={v => setForm(p => ({...p, currency: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{['PLN','EUR','USD','GBP','CHF'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">{lang === 'pl' ? 'Min. zamowienie' : 'Min order'}</Label>
                <Input type="number" value={form.min_order} onChange={e => setForm(p => ({...p, min_order: parseFloat(e.target.value) || 1}))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{lang === 'pl' ? 'Stan magazynowy' : 'Stock'}</Label>
                <Input type="number" value={form.stock_quantity} onChange={e => setForm(p => ({...p, stock_quantity: parseFloat(e.target.value) || 0}))} className="mt-1" /></div>
              <div><Label className="text-xs">{lang === 'pl' ? 'Status' : 'Status'}</Label>
                <Select value={form.active ? 'true' : 'false'} onValueChange={v => setForm(p => ({...p, active: v === 'true'}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">{lang === 'pl' ? 'Aktywny' : 'Active'}</SelectItem>
                    <SelectItem value="false">{lang === 'pl' ? 'Nieaktywny' : 'Inactive'}</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div className="h-px bg-border" />
            <SpecEditor specs={form.specs || []} onChange={specs => setForm(p => ({...p, specs}))} templates={specTemplates} lang={lang} />
            <div className="h-px bg-border" />
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Certyfikaty' : 'Certifications'}</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2">
                {knownCerts.map(cert => {
                  const sel = (form.certifications || []).includes(cert);
                  return (
                    <button key={cert} type="button"
                      onClick={() => setForm(p => ({...p, certifications: sel ? p.certifications.filter(c => c !== cert) : [...(p.certifications || []), cert]}))}
                      className={`text-xs px-2 py-1 rounded-md border transition-colors ${sel ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                      {cert}
                    </button>
                  );
                })}
              </div>
              <Input value={(form.certifications || []).filter(c => !knownCerts.includes(c)).join(', ')}
                onChange={e => { const kn = (form.certifications || []).filter(c => knownCerts.includes(c)); const cu = e.target.value.split(',').map(s => s.trim()).filter(Boolean); setForm(p => ({...p, certifications: [...kn, ...cu]})); }}
                className="mt-1" placeholder={lang === 'pl' ? 'Dodatkowe certyfikaty...' : 'Additional certs...'} />
            </div>
            <div><Label className="text-xs">{lang === 'pl' ? 'Tagi' : 'Tags'}</Label>
              <Input value={(form.tags || []).join(', ')} onChange={e => setForm(p => ({...p, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                className="mt-1" placeholder={lang === 'pl' ? 'np. modern, led, slim, panel' : 'e.g. modern, led, slim, panel'} /></div>
            <Button onClick={handleSave} className="w-full">
              {editProduct ? (lang === 'pl' ? 'Zapisz zmiany' : 'Save changes') : (lang === 'pl' ? 'Dodaj produkt' : 'Add product')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
